import { useEffect, useState } from "react";
import axios from "axios";
import { Flame, ShieldCheck, Clock } from "lucide-react";

const fmt = (v) => `£${Number(v||0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (v) => `${Number(v||0).toFixed(2)}%`;

/**
 * Live "Attack Queue" panel — shows the avalanche/snowball priority order
 * for the next future month, with each debt's effective APR and promo
 * status. Refreshes whenever the parent plan is regenerated.
 */
export default function PriorityQueuePanel({ api, strategy, extraMonthly, refreshKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${api}/payoff-plan/priority-queue`);
        if (!cancelled) setData(res.data);
      } catch { /* silently ignore */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [api, refreshKey]);

  if (!data || !data.queue || data.queue.length === 0) return null;

  const isAvalanche = (data.strategy || strategy) === "avalanche";
  const accruing  = data.queue.filter((q) => (q.effective_apr || 0) > 0);
  const onPromo   = data.queue.filter((q) => q.is_in_promo);

  return (
    <div data-testid="priority-queue-panel" className="border border-gray-200 rounded-sm">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 bg-gray-50">
        <Flame className="w-4 h-4 text-orange-500" strokeWidth={1.75} />
        <h3 className="font-heading text-sm font-semibold tracking-tight uppercase">
          {isAvalanche ? "Avalanche" : "Snowball"} Attack Queue — {data.month_label}
        </h3>
        {extraMonthly > 0 && (
          <span className="text-[10px] text-gray-500 font-mono-data ml-auto">+{fmt(extraMonthly)}/mo surplus</span>
        )}
      </div>
      <div className="px-4 py-2 text-[11px] text-gray-600 border-b border-gray-100">
        <span className="text-orange-700 font-semibold">{accruing.length}</span> debt{accruing.length === 1 ? "" : "s"} actively accruing interest;
        <span className="text-blue-700 font-semibold ml-1">{onPromo.length}</span> still on 0% promo.
        {isAvalanche
          ? <> Surplus payments attack the highest <strong>effective APR</strong> debt first; queue re-sorts as promos expire month-by-month.</>
          : <> Surplus payments knock out the smallest balance first to build momentum.</>
        }
      </div>
      <div className="overflow-x-auto">
        <table className="spreadsheet-table w-full text-xs">
          <thead>
            <tr>
              <th className="text-center w-[60px]">Rank</th>
              <th className="text-left">Debt</th>
              <th className="text-right">Eff. APR</th>
              <th className="text-right">Opening Bal</th>
              <th className="text-right">Min Pay</th>
              <th className="text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.queue.map((q) => {
              const isTopAttack = q.priority_rank === 0 && q.effective_apr > 0;
              const isPromo = q.is_in_promo;
              return (
                <tr key={q.debt_name}
                    data-testid={`queue-row-${q.priority_rank}`}
                    className={isTopAttack ? "bg-orange-50" : (isPromo ? "bg-blue-50/30" : "")}>
                  <td className="text-center font-mono-data font-semibold">
                    {isTopAttack
                      ? <span className="inline-flex items-center justify-center bg-orange-500 text-white w-6 h-6 rounded-full text-[10px]"><Flame className="w-3 h-3" strokeWidth={2} /></span>
                      : q.priority_rank + 1}
                  </td>
                  <td className="font-medium text-gray-800">{q.debt_name}</td>
                  <td className={`numeric font-mono-data ${q.effective_apr > 0 ? "text-orange-700 font-semibold" : "text-gray-400"}`}>
                    {fmtPct(q.effective_apr)}
                  </td>
                  <td className="numeric font-mono-data">{fmt(q.opening_balance)}</td>
                  <td className="numeric font-mono-data text-gray-600">{fmt(q.scheduled_min)}</td>
                  <td className="text-center">
                    {isPromo
                      ? <span className="inline-flex items-center gap-1 text-[10px] text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-sm"><ShieldCheck className="w-3 h-3" strokeWidth={1.75} />0% to {q.promo_end_label}</span>
                      : q.effective_apr > 0
                        ? <span className="inline-flex items-center gap-1 text-[10px] text-orange-700 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded-sm"><Clock className="w-3 h-3" strokeWidth={1.75} />Accruing</span>
                        : <span className="inline-flex items-center gap-1 text-[10px] text-gray-600 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded-sm">Paid off</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {loading && <div className="px-4 py-2 text-[10px] text-gray-400">Refreshing…</div>}
    </div>
  );
}
